import {environment} from '../../../../environments/environment'
import {devTools} from '@ngneat/elf-devtools'
import {createStore, enableElfProdMode} from '@ngneat/elf'
import {
  deleteEntitiesByPredicate,
  getEntity,
  selectEntities,
  selectManyByPredicate,
  UIEntitiesRef,
  unionEntities,
  upsertEntities,
  withEntities,
  withUIEntities,
} from '@ngneat/elf-entities'
import {Message, MessageUI} from '../messaging.types'
import {Inject, Injectable, PLATFORM_ID} from '@angular/core'
import {isPlatformBrowser} from '@angular/common'

@Injectable({
  providedIn: 'root',
})
export class MessageStore {
  private readonly store = createStore(
    {
      name: 'chat-messages-store',
    },
    withEntities<Message>(),
    withUIEntities<MessageUI>(),
  )

  constructor(@Inject(PLATFORM_ID) private readonly platformId: NonNullable<unknown>) {
    if (isPlatformBrowser(this.platformId)) {
      if (environment.elf.devMode) {
        devTools()
      } else {
        enableElfProdMode()
      }
    }
  }

  /**
   * All messages in the store for a room, in ascending order (oldest first).
   */
  allMessagesInRoom$(roomId: string) {
    return this.store
      .combine({
        entities: this.store.pipe(selectManyByPredicate(message => message.roomId === roomId)),
        UIEntities: this.store.pipe(selectEntities({ref: UIEntitiesRef})),
      })
      .pipe(unionEntities())
  }

  /**
   * Replace all messages from a room with a "sent" UI state
   */
  setRoomMessages(roomId: string, messages: Message[]) {
    const ui: MessageUI[] = messages.map(m => ({id: m.id, status: 'sent'}))
    this.store.update(
      deleteEntitiesByPredicate(message => message.roomId === roomId),
      upsertEntities(messages),
      upsertEntities(ui, {ref: UIEntitiesRef}),
    )
  }

  /**
   * Insert or update a message with a "sending" UI state
   */
  upsertMessageBeingSent(message: Message, blob?: File) {
    const ui: MessageUI = {id: message.id, status: 'sending', blob}
    this.store.update(upsertEntities([message]), upsertEntities([ui], {ref: UIEntitiesRef}))
  }

  /**
   * Add a sent message to the store, while removing the optimistic message (with UI state "sending")
   */
  addSentMessage(message: Message, messageOptimisticId: string) {
    const ui: MessageUI = {id: messageOptimisticId, status: 'sent'}
    this.store.update(
      // add the "sent" UI state
      upsertEntities([ui], {ref: UIEntitiesRef}),
      // at this point, we know the message server ID. If we needed the message ID for some subsequent operation, we should update the entity ID in the store
      // at the time of this writing, we don't need the message ID to be the server ID, so we leave it as is
      // the benefit of leaving that is avoiding a repaint of the message, and some glitches with images, where they would load twice
      // updateEntitiesIds(messageOptimisticId, message.id),
    )
  }

  /**
   * Updates the UI component of a message with an "error" UI state
   */
  setMessageFailed(messageOptimisticId: string, blob?: File) {
    const ui: MessageUI = {id: messageOptimisticId, status: 'error', blob}
    this.store.update(upsertEntities(ui, {ref: UIEntitiesRef}))
  }

  /**
   * Insert or update received messages with a "sent" UI state
   */
  upsertReceivedMessage(message: Message, optimisticId: string) {
    if (this.store.query(getEntity(optimisticId))) {
      // user already has the message displayed if we have it
      return
    }

    const ui: MessageUI = {id: message.id, status: 'sent'}
    this.store.update(upsertEntities(message), upsertEntities(ui, {ref: UIEntitiesRef}))
  }

  reset() {
    this.store.reset()
  }
}
